import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Box } from "@mui/material";

// Components
import Layout from "./components/Layout/Layout";
import Queue from "./pages/Queue/Queue";
import Reports from "./pages/Reports/Reports";
import Students from "./pages/Students/Students";
import StudentPage from "./pages/Students/StudentPage";
import ChildPage from "./pages/Students/ChildPage";
import ContactReport from "./pages/Students/ContactReport";
import OverviewTab from "./pages/Students/tabs/OverviewTab";
import ChildrenTab from "./pages/Students/tabs/ChildrenTab";
import SessionsTab from "./pages/Students/tabs/SessionsTab";
import WalletTab from "./pages/Students/tabs/WalletTab";
import TicketsTab from "./pages/Students/tabs/TicketsTab";
import Tutors from "./pages/Tutors/Tutors";
import TutorPage from "./pages/Tutors/TutorPage";
import TutorOverviewTab from "./pages/Tutors/tabs/OverviewTab";
import TutorSessionsTab from "./pages/Tutors/tabs/SessionsTab";
import TutorScheduleTab from "./pages/Tutors/tabs/ScheduleTab";
import TutorRatingsTab from "./pages/Tutors/tabs/RatingsTab";
import TutorPayTab from "./pages/Tutors/tabs/PayTab";
import TutorTicketsTab from "./pages/Tutors/tabs/TicketsTab";
import NotesTab from "./components/StaffNotes/NotesTab";
import Verification from "./pages/Verification/Verification";
import Subjects from "./pages/Subjects/Subjects";
import Plans from "./pages/Plans/Plans";
import Lessons from "./pages/Lessons/Lessons";
import LessonPage from "./pages/Lessons/LessonPage";
import Financial from "./pages/Financial/Financial";
import Payouts from "./pages/Payouts/Payouts";
import Settings from "./pages/Settings/Settings";
import Staff from "./pages/Staff/Staff";
import ActivityLog from "./pages/ActivityLog/ActivityLog";
import Inbox from "./pages/Inbox/Inbox";
import TicketPage from "./pages/Inbox/TicketPage";
import Leads from "./pages/Leads/Leads";
import Notifications from "./pages/Notifications/Notifications";
import NotificationPage from "./pages/Notifications/NotificationPage";
import BookingReminders from "./pages/BookingReminders/BookingReminders";
import Transactions from "./pages/Transactions/Transactions";
import Placeholder from "./pages/Placeholder/Placeholder";
import Login from "./pages/Auth/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { permissions } from "./config/permissions";
import { stripLegacyPrefix } from "./utils/legacyPath";
import ThemeRoot from "./ThemeRoot";

/**
 * Old /adminDashboard/* bookmarks. The app moved to the domain root; these keep
 * their destination instead of dead-ending on a white page.
 */
function LegacyPrefixRedirect() {
  const location = useLocation();
  const to = `${stripLegacyPrefix(location.pathname)}${location.search}${location.hash}`;
  return <Navigate to={to} replace />;
}

function App() {
  return (
    <ThemeRoot>
      {/*
        Do NOT derive this from PUBLIC_URL. `homepage: "."` in package.json
        makes asset paths relative (needed to serve from a subdomain), but it
        also sets PUBLIC_URL to ".", which made this `basename="."`. No route
        matches a path starting with "/", so the whole app rendered blank.
        The two concerns are separate: PUBLIC_URL is for assets, this is for
        routes. Set REACT_APP_ROUTER_BASENAME only if it moves back under a
        sub-path like /adminDashboard.
      */}
      <Router basename={process.env.REACT_APP_ROUTER_BASENAME || "/"}>
        <AuthProvider>
          <Box sx={{ display: "flex" }}>
            <Routes>
              <Route path="/login" element={<Login />} />
              {/*
                Everything the old host served under /adminDashboard.
                Ranked above "/*" by specificity, so it wins.
              */}
              <Route path="/adminDashboard/*" element={<LegacyPrefixRedirect />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_DASHBOARD}
                            >
                              <Queue />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_REPORTS}
                            >
                              <Reports />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/students"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_STUDENTS}
                            >
                              <Students />
                            </ProtectedRoute>
                          }
                        />
                        {/* 059 D11: a static segment, so it outranks /students/:id. */}
                        <Route
                          path="/students/contact-report"
                          element={
                            <ProtectedRoute requiredPermission={permissions.VIEW_STUDENTS}>
                              <ContactReport />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/students/:id"
                          element={
                            <ProtectedRoute requiredPermission={permissions.VIEW_STUDENTS}>
                              <StudentPage />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<OverviewTab />} />
                          <Route path="children" element={<ChildrenTab />} />
                          <Route path="sessions" element={<SessionsTab />} />
                          <Route path="wallet" element={<WalletTab />} />
                          <Route path="tickets" element={<TicketsTab />} />
                          <Route path="notes" element={<NotesTab />} />
                        </Route>
                        <Route
                          path="/students/:id/children/:childId"
                          element={
                            <ProtectedRoute requiredPermission={permissions.VIEW_STUDENTS}>
                              <ChildPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tutors"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_TUTORS}
                            >
                              <Tutors />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/tutors/:id"
                          element={
                            <ProtectedRoute requiredPermission={permissions.VIEW_TUTORS}>
                              <TutorPage />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<TutorOverviewTab />} />
                          <Route path="sessions" element={<TutorSessionsTab />} />
                          <Route path="schedule" element={<TutorScheduleTab />} />
                          <Route path="ratings" element={<TutorRatingsTab />} />
                          <Route path="pay" element={<TutorPayTab />} />
                          <Route path="tickets" element={<TutorTicketsTab />} />
                          <Route path="notes" element={<NotesTab />} />
                        </Route>
                        <Route
                          path="/verification"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.REVIEW_VERIFICATION}
                            >
                              <Verification />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/subjects"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_SUBJECTS}
                            >
                              <Subjects />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/plans"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_PLANS}
                            >
                              <Plans />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/lessons"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_LESSONS}
                            >
                              <Lessons />
                            </ProtectedRoute>
                          }
                        />
                        {/* One lesson (047 D92): the list's rows and every lesson link open it. */}
                        <Route
                          path="/lessons/:lessonId"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_LESSONS}
                            >
                              <LessonPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/financial"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_FINANCIAL}
                            >
                              <Financial />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/payouts"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_FINANCIAL}
                            >
                              <Payouts />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/transactions"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_FINANCIAL}
                            >
                              <Transactions />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/staff"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.MANAGE_STAFF}
                            >
                              <Staff />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_SETTINGS}
                            >
                              <Settings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/leads"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_LEADS}
                            >
                              <Leads />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/referrals"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_STUDENTS}
                            >
                              <Placeholder
                                caption="Growth"
                                title="Referrals"
                                description="Referrers ranked by friends brought and sessions earned, with each friend's milestone and the reward rows. Read-only."
                              />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.SEND_NOTIFICATIONS}
                            >
                              <Notifications />
                            </ProtectedRoute>
                          }
                        />
                        {/* New notification, and one announcement to edit or read (044 D45). */}
                        <Route
                          path="/notifications/new"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.SEND_NOTIFICATIONS}
                            >
                              <NotificationPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications/:id"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.SEND_NOTIFICATIONS}
                            >
                              <NotificationPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* 055 D20: the automatic booking reminders, beside Notifications. */}
                        <Route
                          path="/booking-reminders"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.SEND_NOTIFICATIONS}
                            >
                              <BookingReminders />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/activity-log"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_ACTIVITY_LOG}
                            >
                              <ActivityLog />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/inbox"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_INBOX}
                            >
                              <Inbox />
                            </ProtectedRoute>
                          }
                        />
                        {/* One ticket (047 D54): the list's rows and every ticket link open it. */}
                        <Route
                          path="/inbox/:ticketId"
                          element={
                            <ProtectedRoute
                              requiredPermission={permissions.VIEW_INBOX}
                            >
                              <TicketPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* Tickets is now Inbox (spec 026 S3) — old links/bookmarks still land. */}
                        <Route path="/tickets" element={<Navigate to="/inbox" replace />} />
                        {/*
                          Anything else goes home. Without this an unknown path
                          — a stale bookmark, or /schedule after D7 removed it —
                          rendered the shell with an empty content pane and no
                          hint that the URL was wrong.
                        */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Box>
        </AuthProvider>
      </Router>
    </ThemeRoot>
  );
}

export default App;
